/**
 * yearRange — the year interval a pin occupies on the timeline.
 *
 * Looks a normalised period word (Stenalder, Bronzealder, …) up against the
 * country's own chronology in data/periodRanges.ts and answers [y0, y1]
 * in calendar years, negative for BC. Udateret, Sami and anything the table
 * does not know answer null, and such a pin stays out of the slider's three
 * states and is drawn as undated. Written into GeoJSON properties by
 * utils/geojson.ts as two scalars, y0 and y1, because MapLibre stringifies
 * arrays in a property bag. Read back by hooks/useGeoJsonLayers.ts.
 */
import type { Country } from "../types/index.ts";
import { PERIOD_RANGES } from "../data/periodRanges.ts";
import { formatBcYear } from "./era.ts";

/**
 * Period words that name no span of years, however the register uses them.
 *
 * "Oldtid" is here because it is this app's word for «prehistoric, and the
 * register does not say which period»: heritage.ts falls back to it for a
 * Norwegian locality whose children carry no datering, Sweden's fetcher
 * rewrites an undated ancient remain to it, and Denmark's register issues it
 * as a value in its own right. It is also the commonest word on the map —
 * 1 332 of 2 000 Danish sites, every Greenlandic one, 1 095 of 2 000 Irish,
 * measured 3 Sep 2026 — and its span is the widest a country's chronology
 * has. Giving it years let a quarter of a million of them fade in behind a
 * gradient, which reads as an answer where the register gave none. Undated
 * pins stay on the map at half strength while the bar is open.
 */
const UNDATED = new Set(["", "Udateret", "Sami", "Ukendt", "Unknown", "Oldtid"]);

/**
 * Words the country tables do not all carry. "Roman" is emitted by the
 * British and Dutch fetchers and sits in GB/NL/IM; a Scandinavian register
 * that says it means the Roman Iron Age, which the Danish table folds into
 * Jernalder, so the fallback span is that one.
 */
const FALLBACK: Record<string, [number, number]> = { Roman: [0, 400] };

/**
 * Words a register emits that mean a period the table already has.
 *
 * Measured across eleven registers on 4 Sep 2026: 206 pins in the sample
 * carried one of these and got no span at all, most of them Welsh. The lower
 * case «Historisk tid» is Cadw writing the same word differently; «Nyere tid»
 * and «Efterreformatorisk tid» are what the Danish register calls
 * post-Reformation; «Romertid» is the Roman row. «Tidlig middelalder» is
 * Cadw's Early Medieval, 410 to 1066, which in this app's vocabulary is the
 * Vikingetid row — that is what the British table labels it.
 *
 * This is the timeline's reading only. The pin's colour and the period chips
 * still go by the raw word, so these five keep the fallback colour; that is
 * older than the timeline and is not fixed here.
 */
const ALIASES: Record<string, string> = {
  "historisk tid": "Historisk Tid",
  "nyere tid": "Historisk Tid",
  "efterreformatorisk tid": "Historisk Tid",
  "romertid": "Roman",
  "tidlig middelalder": "Vikingetid",
};

/**
 * The register's word in this app's vocabulary, so one period is one thing
 * everywhere: the pin's colour (utils/mapExpressions matches on it), the
 * period chips, the panel's badge and the timeline. Before this the five
 * spellings above kept the fallback colour and fell out of every chip, which
 * is how 126 Welsh sites sat on the map in a purple that means «unknown
 * period» while the register had said post-medieval all along.
 */
export function normalisePeriod(period: string | undefined): string {
  const raw = (period ?? "").trim();
  return ALIASES[raw.toLowerCase()] ?? raw;
}

export function yearRangeFor(period: string | undefined, country: Country | string | undefined): [number, number] | null {
  const raw = (period ?? "").trim();
  const p = normalisePeriod(raw);
  if (UNDATED.has(p)) return null;
  // Country first, then the Danish table as periodsByCountry does, then the
  // words no country table carries.
  const range = PERIOD_RANGES[(country ?? "DK") as Country]?.[p] ?? PERIOD_RANGES.DK?.[p] ?? FALLBACK[p];
  if (!range) return null;
  const [a, b] = range;
  return a <= b ? [a, b] : [b, a];
}

/** The two scalars for a property bag, or nothing at all when undated. */
export function yearProps(period: string | undefined, country: Country | string | undefined): { y0: number; y1: number } | Record<string, never> {
  const r = yearRangeFor(period, country);
  return r ? { y0: r[0], y1: r[1] } : {};
}

/**
 * Where a pin stands relative to a year. A register period says when a site
 * was built, at era resolution, and a protected monument still stands, so:
 * before y0 it is not yet built; between y0 and y1 it is emerging, the
 * register cannot say whether it stood yet, and the map fades it in over the
 * span rather than snapping; after y1 it is in. "after" is gone, and only a
 * hand-dated site carries the y2 (end of use) that can say so, since the
 * registers list what exists today.
 */
export type TimelineState = "before" | "emerging" | "in" | "after";

export function timelineState(y0: number, y1: number, year: number, y2?: number): TimelineState {
  if (year < y0) return "before";
  if (y2 != null && year > y2) return "after";
  if (year <= y1) return "emerging";
  return "in";
}

/** 0 at y0, 1 at y1: how far into its build span a year is. */
export function emergence(y0: number, y1: number, year: number): number {
  if (y1 <= y0) return 1;
  return Math.max(0, Math.min(1, (year - y0) / (y1 - y0)));
}

/** A calendar year for the reader: "2800 BC" / "2800 f.Kr." below 1, plain above. */
export function formatYear(year: number, lang: string): string {
  return year < 1 ? formatBcYear(year === 0 ? -1 : year, lang) : String(year);
}

/**
 * The hand-dated line for a panel: "built 3200–3100 BC, abandoned c. 2900 BC"
 * with the register's own words for the two verbs passed in.
 */
export function describeSiteDate(d: { y0: number; y1: number; y2?: number }, lang: string, words: { built: string; abandoned: string }): string {
  const span = d.y0 === d.y1 ? formatYear(d.y0, lang) : `${formatYear(d.y0, lang)}–${formatYear(d.y1, lang)}`;
  const out = `${words.built} ${span}`;
  return d.y2 != null ? `${out}, ${words.abandoned} ${formatYear(d.y2, lang)}` : out;
}

/**
 * Years from a building register's own dating string. The registers write
 * it four ways and this reads all of them: a year ("1920"), a span in any
 * dash ("1875-1899", "1760–1770", "1840 - 1859"), a century in Nordic
 * ("1700-tallet", "1700-talet", "1700-luvun") or English ("18th century")
 * form, and a circa prefix, which is dropped. Anything before 800 or after
 * this year is a code, not a date, and answers null.
 */
export function yearsFromDating(text: string | number | undefined): [number, number] | null {
  if (text == null) return null;
  const t = String(text).trim();
  if (!t) return null;
  const ok = (y: number) => y >= 800 && y <= new Date().getFullYear();
  const century = t.match(/^(?:ca\.?\s*)?(\d{2})(\d{2})-(?:tallet|talet|luku|luvun|luvulta)/i);
  if (century) { const c = Number(century[1] + "00"); return ok(c) ? [c, c + 99] : null; }
  const english = t.match(/^(?:c\.?\s*)?(\d{1,2})(?:st|nd|rd|th)\s+century/i);
  if (english) { const c = (Number(english[1]) - 1) * 100; return ok(c) ? [c, c + 99] : null; }
  // A full four-digit span decides on its own when it is wrong: a reversed
  // "1899-1875" is a data error and answering it with 1899 would be a guess.
  const span = t.match(/(\d{4})\s*[-–—]\s*(\d{4})/);
  if (span) { const a = Number(span[1]), b = Number(span[2]); if (!(ok(a) && ok(b) && a <= b)) return null; }
  const german = germanYears(t, ok);
  if (german) return german;
  const year = t.match(/^(?:ca\.?|c\.?|omkring|about|noin|um)?\s*(\d{4})\b/i);
  if (year) { const y = Number(year[1]); return ok(y) ? [y, y] : null; }
  return null;
}

/**
 * The part of a century a German register names, as a span of years inside it.
 *
 * "Anfang" and "Ende" are read as thirds rather than as the first and last
 * decade, which is how the Denkmalpflege literature uses them; a half is
 * fifty years, a Drittel thirty-three and a Viertel twenty-five, with the
 * last one running to 99 so no year falls between two subdivisions.
 */
function centuryPart(text: string, c: number): [number, number] {
  const named = text.match(/\b(Anfang|Mitte|Ende)\b/i)?.[1]?.toLowerCase();
  if (named === "anfang") return [c, c + 32];
  if (named === "mitte") return [c + 33, c + 66];
  if (named === "ende") return [c + 67, c + 99];
  const frac = text.match(/\b([1-4])\.\s*(H\u00e4lfte|Drittel|Viertel)/i);
  if (frac) {
    const k = Number(frac[1]);
    const width = /h/i.test(frac[2][0]) ? 50 : /d/i.test(frac[2][0]) ? 33 : 25;
    const start = c + (k - 1) * width;
    const end = start + width - 1;
    return [start, Math.min(end, c + 99)];
  }
  return [c, c + 99];
}

/**
 * German dating strings, as the Länder actually write them.
 *
 * Measured over the 21.303 filled `dating` values in the Berlin and Hamburg
 * registers on 5 Sep 2026, where thirty shapes cover 19.577: a bare year,
 * "1910-11", "1919 / 1920", "1885, um", "um 1895", "18. Jh.",
 * "19. Jh., Ende", "1. H\u00e4lfte 19. Jh.", "1920er Jahre", "nach 1918", and
 * Sachsen's "Kern 1749 (Kirche); nach 1955".
 *
 * The earliest match in the string wins, not the most specific one. That is
 * the whole point of the pass: a Saxon record's first clause is the building
 * and everything after a semicolon is later work, so a rule that preferred a
 * span would date "Kern 1749 (Kirche); Umbau 1955-1960" to the rebuilding.
 * Only the forms the app's other registers do not already answer reach this
 * function; the Nordic and English ones are handled above it.
 */
function germanYears(t: string, ok: (y: number) => boolean): [number, number] | null {
  const hits: { at: number; range: [number, number] }[] = [];
  const push = (at: number, a: number, b: number) => { if (ok(a) && ok(b) && a <= b) hits.push({ at, range: [a, b] }); };

  // "17./18. Jh." is both centuries, and the number before the slash is the
  // one the plain century match below would miss.
  const twoCent = t.match(/(\d{1,2})\.\s*\/\s*(\d{1,2})\.\s*(?:Jh\.?|Jahrhundert)/i);
  if (twoCent) push(twoCent.index ?? 0, (Number(twoCent[1]) - 1) * 100, (Number(twoCent[2]) - 1) * 100 + 99);
  const cent = t.match(/(\d{1,2})\.\s*(?:Jh\.?|Jahrhundert)/i);
  if (cent && !twoCent) {
    const c = (Number(cent[1]) - 1) * 100;
    const [a, b] = centuryPart(t, c);
    // The subdivision may stand before the century ("Mitte 18. Jh.") or after
    // it ("19. Jh., Ende"), so it is read from the whole string and the match
    // is anchored at whichever of the two comes first.
    const sub = t.match(/\b(Anfang|Mitte|Ende|[1-4]\.\s*(?:H\u00e4lfte|Drittel|Viertel))/i);
    push(Math.min(cent.index ?? 0, sub?.index ?? Number.MAX_SAFE_INTEGER), a, b);
  }
  const decade = t.match(/(\d{4})er(?:\s*Jahre?)?/i);
  if (decade) push(decade.index ?? 0, Number(decade[1]), Number(decade[1]) + 9);
  const slash = t.match(/(\d{4})\s*\/\s*(\d{4})/);
  if (slash) push(slash.index ?? 0, Number(slash[1]), Number(slash[2]));
  // "1910-11": the end is written with the century left off.
  const shortSpan = t.match(/(\d{4})\s*[-–—/]\s*(\d{2})(?!\d)/);
  if (shortSpan) {
    const a = Number(shortSpan[1]);
    let b = Math.floor(a / 100) * 100 + Number(shortSpan[2]);
    if (b < a) b += 100;
    push(shortSpan.index ?? 0, a, b);
  }
  const span = t.match(/(\d{4})\s*[-–—]\s*(\d{4})/);
  if (span) push(span.index ?? 0, Number(span[1]), Number(span[2]));
  const bare = t.match(/\b(\d{4})\b/);
  if (bare) push(bare.index ?? 0, Number(bare[1]), Number(bare[1]));

  if (!hits.length) return null;
  // Earliest first; on a tie the wider match wins, so "1910-11" beats the
  // bare 1910 that starts at the same offset.
  hits.sort((x, y) => x.at - y.at || (y.range[1] - y.range[0]) - (x.range[1] - x.range[0]));
  return hits[0].range;
}

/**
 * Layers with no dating field of their own, dated from what they are.
 * Runestones are Viking Age in the country's own span; ogham stones and
 * Pictish stones are earlier and named by their type. A megalith is a
 * Neolithic tomb unless the type says stone circle or standing stone, which
 * run on into the Bronze Age. An event has one year, read off the front of
 * its date, «ca.» and ranges included. A Stolperstein carries the year of
 * the deportation it marks where the text names one, else the war years.
 */
export function runestoneYears(type: string | undefined, country: Country | string | undefined): [number, number] | null {
  const t = (type ?? "").toLowerCase();
  if (t.includes("ogham")) return [400, 600];
  if (t.includes("pictish")) return [500, 900];
  return yearRangeFor("Vikingetid", country);
}

export function eventYears(date: string | undefined): [number, number] | null {
  const m = (date ?? "").match(/(-?\d{3,4})/);
  if (!m) return null;
  const y = Number(m[1]);
  return y >= -1000 && y <= new Date().getFullYear() ? [y, y] : null;
}

export function megalithYears(type: string | undefined, period: string | undefined, country: Country | string | undefined): [number, number] | null {
  const fromPeriod = yearRangeFor(period, country);
  if (fromPeriod) return fromPeriod;
  const t = (type ?? "").toLowerCase();
  if (/circle|cirkel|krets|ring|menhir|standing|bauta|rejst|resta/.test(t)) return [-3000, -500];
  return [-3700, -2800];
}

export function stolpersteinYears(deportDate: string | undefined, deathYear: string | undefined): [number, number] {
  const y = `${deportDate ?? ""} ${deathYear ?? ""}`.match(/\b(19[34]\d)\b/);
  return y ? [Number(y[1]), Number(y[1])] : [1940, 1945];
}
